import { useEffect, useMemo, useRef, useState } from 'react';

import { Text } from '@spotify-internal/encore-web';

import {
  ToolExtensionOptions,
  ToolGroupEntry,
} from '../../extensions/tool-extension';
import { useToolExtensions } from '../../extensions/tool-extension';
import { ElapsedTimer } from '../ElapsedTimer';
import { collapseConsecutiveTools } from './collapseConsecutiveTools';
import { formatDefaultToolLabel, inferToolIcon } from './defaultToolMappings';
import { sharedToolStyles } from './sharedStyles';

import styles from './InlineToolGroup.module.css';

const FADE_MS = 200;
const HOLD_MS = 500;

type Phase = 'hold' | 'fading-out' | 'fading-in';

const InlineToolEntry = ({
  entry,
  ext,
  isStreaming,
}: {
  entry: ToolGroupEntry;
  ext: ToolExtensionOptions | undefined;
  isStreaming: boolean;
}) => {
  const IconComponent = ext?.icon ?? inferToolIcon(entry.tool);

  if (ext?.renderLabel) {
    return (
      <>
        <IconComponent size={14} className={sharedToolStyles.icon} />
        {ext.renderLabel(entry, {
          isStreaming: entry.isStreaming ?? isStreaming,
        })}
      </>
    );
  }

  const label = ext?.formatLabel
    ? ext.formatLabel(entry)
    : formatDefaultToolLabel(entry);

  return (
    <>
      <IconComponent size={14} className={sharedToolStyles.icon} />
      <Text variant="bodySmall" semanticColor="textSubdued">
        {label.text}
      </Text>
      {!entry.result && entry.startedAt !== undefined && (
        <ElapsedTimer startedAt={entry.startedAt} />
      )}
    </>
  );
};

const AnimatedCountBadge = ({ count }: { count: number }) => {
  const [shown, setShown] = useState(count);
  const [phase, setPhase] = useState<Phase>('hold');
  const countRef = useRef(count);
  const rafRef = useRef<number>();
  countRef.current = count;

  useEffect(() => {
    if (phase === 'hold') {
      if (shown >= count) return undefined;
      const timer = setTimeout(() => setPhase('fading-out'), HOLD_MS);
      return () => clearTimeout(timer);
    }

    if (phase === 'fading-out') {
      const timer = setTimeout(() => {
        setShown(prev => Math.min(prev + 1, countRef.current));
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = requestAnimationFrame(() => {
            setPhase('fading-in');
          });
        });
      }, FADE_MS);
      return () => {
        clearTimeout(timer);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
    }

    if (phase === 'fading-in') {
      const timer = setTimeout(() => setPhase('hold'), FADE_MS);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [phase, shown, count]);

  if (count <= 1) return null;

  return (
    <span
      className={styles.badge}
      style={{ '--fade-ms': `${FADE_MS}ms` } as React.CSSProperties}
    >
      <span
        className={styles.countNumber}
        style={{ opacity: phase === 'fading-out' ? 0 : 1 }}
      >
        {shown}
      </span>
    </span>
  );
};

export const InlineToolGroup = ({
  entries,
  isStreaming,
  collapseConsecutiveCalls,
}: {
  entries: ToolGroupEntry[];
  isStreaming: boolean;
  collapseConsecutiveCalls: boolean;
}) => {
  const toolExtensions = useToolExtensions();

  const collapsedEntries = useMemo(
    () =>
      collapseConsecutiveTools(entries, entry => {
        const ext = toolExtensions.find(e => e.predicate(entry.tool));
        return (
          collapseConsecutiveCalls && ext?.collapseConsecutiveCalls !== false
        );
      }),
    [entries, toolExtensions, collapseConsecutiveCalls],
  );

  return (
    <div className={styles.list} role="group" aria-label="Tool calls">
      {collapsedEntries.map((collapsed, i) => {
        const ext = toolExtensions.find(e => e.predicate(collapsed.entry.tool));

        return (
          <div key={i} className={styles.item}>
            <InlineToolEntry
              entry={collapsed.entry}
              ext={ext}
              isStreaming={isStreaming}
            />
            <AnimatedCountBadge count={collapsed.count} />
          </div>
        );
      })}
    </div>
  );
};
